/**
 * CoursePage Component
 *
 * A comprehensive React component that manages the course view and access flow for educational content.
 * Handles course information retrieval, user validation, access control, and conditional rendering
 * for published/unpublished courses and loading states.
 *
 * Features:
 * - Course information retrieval and display with multiple data sources
 * - User validation for name confirmation and access permissions
 * - Conditional rendering for published/unpublished courses
 * - Loading state management during data fetching
 * - Integration with Redux for centralized state management
 * - Navigation and redirects for disabled courses and academies
 * - LTI (Learning Tools Interoperability) integration support
 * - User progress tracking and missing activities monitoring
 * - Responsive design with different layouts based on access level
 *
 * @param {Object} props - Component properties
 * @param {number} [props.idCourse] - Course identifier for data retrieval
 * @param {Object} [props.ltiData] - LTI integration data for external learning platforms
 * @returns {JSX.Element} Rendered CoursePage component with course info and conditional views
 */

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';

import NoAccess from 'components/LessonPage/NoAccess/NoAccess';

import { academyDataActions } from '../../../store/academy-slice';

import {
  getAccesCourse,
  getCourseAditionalInfo,
  getCourseInfo,
  getCourseReviews,
  getDegree,
  getLessonsByCourse,
  getMissingActivities,
  getTrailerCourseInfo,
  getUserAditionalTrackCourse,
  getUsersInCourse
} from 'store/course-actions';

import {
  confirmNameValidate,
  getValidationName
} from 'store/user-actions';

import Loading from 'components/UI/Loading/Loading';

import CourseTop from './CourseTop/TopSection/CourseTop';
import CourseSpecificInfo from './CourseSpecificInfo/CourseSpecificInfo';
import CourseBottom from './CourseBottom/CourseBottom';
import BreadCrumbs from 'components/UI/BreadCrumbs/BreadCrumbs';
import ValidateName from './ValidateName/ValidateName';

import classes from './CoursePage.module.css';

/**
 * Extract URL path segments for routing logic and navigation context
 * Used to determine current page structure and conditional rendering
 */
const url = window.location.pathname.split('/');

const CoursePage = props => {
  const dispatch = useDispatch();
  // Component state management
  const [loadData, setLoadData] = useState(false); // Data loading completion flag
  const [actuallyIdCourse, setActuallyIdCourse] = useState(0); // Currently loaded course ID
  const [isLoading, setIsLoading] = useState(true); // Loading state for UI
  const [openValidationName, setOpenValidationName] =
    useState(false); // Name validation dialog state

  // Redux state selectors for course, academy, and user data
  const dataCourse = useSelector(state => state.courseInfo);
  const dataAcademy = useSelector(state => state.academyInfo);
  const userInfo = useSelector(state => state.userInfo);

  /**
   * Initial banner display effect
   * Shows the academy banner when the component mounts
   */
  useEffect(() => {
    dispatch(
      academyDataActions.showBanner({
        showBanner: true
      })
    );
  }, []);

  /**
   * Data fetching effect
   * Retrieves all necessary course and user information when the course or academy changes
   * Includes course details, validation, tracking, lessons, reviews, and access permissions
   */
  useEffect(() => {
    if (!loadData && dataAcademy.data.idAcademy) {
      dispatch(getCourseInfo(props.idCourse));
      dispatch(getValidationName(props.idCourse));
      dispatch(getCourseAditionalInfo(props.idCourse));
      dispatch(getUsersInCourse(props.idCourse));
      dispatch(getUserAditionalTrackCourse(props.idCourse));
      dispatch(getLessonsByCourse(props.idCourse));
      dispatch(getCourseReviews(props.idCourse));
      dispatch(getDegree(props.idCourse));
      dispatch(getMissingActivities(props.idCourse));
      if (dataAcademy.data.idAcademy) {
        dispatch(
          getTrailerCourseInfo(
            dataAcademy.data.idAcademy,
            props.idCourse
          )
        );
        dispatch(
          getAccesCourse(props.idCourse, dataAcademy.data.idAcademy)
        );
      }
      setLoadData(true);
    }
  }, [props.idCourse, dataAcademy.data]);

  /**
   * Course data validation effect
   * Sets the actual course ID when all required data is loaded
   * Validates that all essential course components are available
   */
  useEffect(() => {
    if (
      dataCourse.data.emisor &&
      dataCourse.aditionalData.name &&
      dataCourse.userAditionalTrack.userTrack &&
      dataCourse.lessonsCourse.lessonExtraInfo &&
      dataCourse.courseReviews.reviewsStars &&
      dataCourse.missingActivities
    ) {
      setActuallyIdCourse(dataCourse.data.idLista);
    }
  }, [
    dataCourse.data,
    dataCourse.aditionalData,
    dataCourse.userAditionalTrack,
    dataCourse.lessonsCourse,
    dataCourse.courseReviews,
    dataCourse.missingActivities
  ]);

  /**
   * Access control effect
   * Redirects the user if the course is disabled and the academy is closed
   */
  useEffect(() => {
    if (
      dataCourse.accessCourse === false &&
      dataAcademy.data.isOpen === 0
    ) {
      window.location.href = `/${dataAcademy.data.shortName}/disabled`;
    }
  }, [dataCourse.accessCourse, dataAcademy.isOpen]);

  /**
   * Loading state effect
   * Disables loading when the actual course ID matches the prop
   */
  useEffect(() => {
    if (props.idCourse == actuallyIdCourse) {
      setIsLoading(false);
    }
  }, [props.idCourse, actuallyIdCourse]);

  /**
   * User name validation effect
   * Opens the name validation dialog if the user is missing a valid name
   */
  useEffect(() => {
    if (userInfo.data.usuario) {
      if (
        !userInfo.validationName &&
        userInfo.validationName !== null
      ) {
        setOpenValidationName(true);
      }
    }
  }, [userInfo]);

  /**
   * Handles user name confirmation
   * Closes the validation dialog upon successful confirmation
   */
  const confirmNameHandler = () => {
    (async () => {
      const confirmNameData = await confirmNameValidate(
        props.idCourse
      );
      if (confirmNameData) {
        setOpenValidationName(false);
      }
    })();
  };

  return (
    <>
      {/* Main content - Conditional rendering based on loading and authorization */}
      {!isLoading ? (
        dataCourse.aditionalData.authorized === 1 ? (
          <section className={classes.coursePageContainer}>
            {!dataCourse.accessCourse &&
            dataAcademy.data.productType === 2 ? (
              <NoAccess />
            ) : (
              <>
                {/* Breadcrumbs navigation, only if not in course URL */}
                {url[2] !== 'course' && (
                  <BreadCrumbs
                    title={dataCourse.data.nombre} // Course name for breadcrumb display
                  />
                )}
                {/* Top section with course hero and main information */}
                <CourseTop
                  dataCourse={dataCourse} // Complete course data including basic info, pricing, and metadata
                />
                {/* Specific course information and metadata */}
                <CourseSpecificInfo
                  dataCourse={dataCourse} // Course data for displaying detailed specifications and features
                />
                {/* Bottom section with course details and LTI data */}
                <CourseBottom
                  dataCourse={dataCourse} // Course data for content, materials, and progress tracking
                  ltiData={props.ltiData} // LTI integration data for grade passback and external platform communication
                />
                {/* Name validation dialog for user confirmation */}
                <ValidateName
                  open={openValidationName} // Boolean to control dialog visibility state
                  confirmName={confirmNameHandler} // Callback function to handle name confirmation action
                />
              </>
            )}
          </section>
        ) : (
          /* Unpublished course message and navigation */
          <div className={classes.noCourseContainer}>
            <p className={classes.title}>
              Esta versión del curso ya no se encuentra disponible
            </p>
            <p className={classes.subtitle}>
              Pero no te preocupes. Puedes ver la lista actualizada
              de cursos regresando al inicio de la academia.
            </p>
            <Button
              component={Link}
              to="/"
              className={classes.btnHome}
            >
              Inicio
            </Button>
          </div>
        )
      ) : (
        /* Loading state while fetching course data */
        <Loading />
      )}
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CoursePage.propTypes = {
  /** Course identifier for data retrieval and API calls */
  idCourse: PropTypes.number,
  /** LTI integration data for external learning platforms and grade passback */
  ltiData: PropTypes.object
};

CoursePage.defaultProps = {
  idCourse: undefined,
  ltiData: undefined
};

export default CoursePage;
